/**
 * The Desktop app screen's form state and the rules it shares with the server.
 *
 * Every field is a string, the file size included: a number input bound to a
 * number cannot hold the intermediate states of typing ("", "7."), and coercing
 * on each keystroke fights the user. Conversion happens once, on save.
 */
import type { DesktopBuild, DesktopReleaseUpdate, DesktopReleaseView } from '../../api/schema';

export interface BuildForm {
  url: string;
  altUrl: string;
  sizeMb: string;
  minOs: string;
}

export interface ReleaseForm {
  version: string;
  notesUrl: string;
  published: boolean;
  windows: BuildForm;
  macos: BuildForm;
}

const EMPTY_BUILD: BuildForm = { url: '', altUrl: '', sizeMb: '', minOs: '' };

function toBuildForm(build: DesktopBuild | null | undefined): BuildForm {
  if (build === null || build === undefined) return { ...EMPTY_BUILD };
  return {
    url: build.url,
    altUrl: build.alt_url ?? '',
    sizeMb: build.size_mb === null || build.size_mb === undefined ? '' : String(build.size_mb),
    minOs: build.min_os ?? '',
  };
}

export function toForm(release: DesktopReleaseView): ReleaseForm {
  return {
    version: release.version ?? '',
    notesUrl: release.notes_url ?? '',
    published: release.published,
    windows: toBuildForm(release.windows),
    macos: toBuildForm(release.macos),
  };
}

function trimToNull(value: string): string | null {
  const trimmed = value.trim();
  return trimmed === '' ? null : trimmed;
}

function toBuildPayload(build: BuildForm): DesktopReleaseUpdate['windows'] {
  // No installer means the platform is not offered; sending the rest would ask
  // the server to store a size against nothing.
  if (build.url.trim() === '') return null;
  const size = Number.parseFloat(build.sizeMb);
  return {
    url: build.url.trim(),
    alt_url: trimToNull(build.altUrl),
    size_mb: Number.isFinite(size) && size > 0 ? size : null,
    min_os: trimToNull(build.minOs),
  };
}

export function toPayload(form: ReleaseForm): DesktopReleaseUpdate {
  return {
    version: trimToNull(form.version),
    notes_url: trimToNull(form.notesUrl),
    published: form.published,
    windows: toBuildPayload(form.windows),
    macos: toBuildPayload(form.macos),
  };
}

/**
 * Mirrors the server's rule. Blank is not invalid — an absent link is how a
 * platform, or the release notes, is left off.
 */
export function isAcceptableUrl(value: string): boolean {
  const trimmed = value.trim();
  if (trimmed === '') return true;
  return /^https:\/\/.+/i.test(trimmed);
}

export function everyUrlAcceptable(form: ReleaseForm): boolean {
  return [form.notesUrl, form.windows.url, form.windows.altUrl, form.macos.url, form.macos.altUrl].every(
    isAcceptableUrl,
  );
}

/**
 * Whether the record may go live. One platform is a complete release — builds
 * do not always ship together, and offering the one that exists beats offering
 * neither. The server enforces the same rule; this only decides whether the
 * switch is usable.
 */
export function canPublish(form: ReleaseForm): boolean {
  return (
    form.version.trim() !== '' &&
    (form.windows.url.trim() !== '' || form.macos.url.trim() !== '') &&
    everyUrlAcceptable(form)
  );
}

export function isDirty(a: ReleaseForm, b: ReleaseForm): boolean {
  return JSON.stringify(a) !== JSON.stringify(b);
}

/** "95 MB" — omitted entirely rather than printed as "0 MB" when unset. */
export function sizeLabel(sizeMb: number | string | null | undefined): string | null {
  const value = typeof sizeMb === 'string' ? Number.parseFloat(sizeMb) : sizeMb;
  if (value === null || value === undefined || !Number.isFinite(value) || value <= 0) return null;
  return `${String(Math.round(value * 10) / 10)} MB`;
}
